import { Calculator } from 'lucide-react'

import { priceExamples } from '@iws/theme-schema/campaign'
import { formatCurrency } from '@iws/theme-schema/currency'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { OfferModel } from '@/types'
import type { OfferCatalog } from '@/hooks/features/campaigns'

interface Props {
  type: OfferModel.OfferType
  rules: Partial<OfferModel.OfferRules>
  catalog: OfferCatalog
  currency?: string
}

/** "Customer pays TK 900 instead of TK 1,200": computed with the storefront's pricing code (FR-CB5). */
export const PriceExample = ({ type, rules, catalog, currency }: Props) => {
  const examples = priceExamples(type, rules, catalog.prices)
  const money = (n: number) => formatCurrency(n, currency)

  return (
    <Card aria-live="polite">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="size-4 text-text-2" aria-hidden /> What the customer pays
        </CardTitle>
      </CardHeader>
      <CardContent>
        {catalog.loading ? (
          <p className="text-sm text-muted-foreground">Loading prices…</p>
        ) : examples.length === 0 ? (
          <p className="text-sm text-muted-foreground">Choose the products and the discount to see an example price.</p>
        ) : (
          <ul className="space-y-2">
            {examples.map((e, i) => (
              <li key={i} className="text-sm">
                Buying {e.basket}: pays <strong className="font-semibold tabular-nums">{money(e.pays)}</strong>
                {e.saving > 0 ? (
                  <>
                    {' '}instead of <span className="tabular-nums text-muted-foreground line-through">{money(e.regular)}</span>{' '}
                    <span className="font-medium text-success-dark">(saves {money(e.saving)})</span>
                  </>
                ) : (
                  <span className="text-warning-dark"> — no saving yet</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
